import React, { useState, useEffect, useCallback, PropsWithChildren } from 'react';

import { performanceLogger } from './logger';

// Thresholds for render performance (in milliseconds)
const RENDER_WARNING_THRESHOLD = 16; // 60fps = 16.67ms per frame
const RENDER_SEVERE_WARNING_THRESHOLD = 50; // Very slow render

// Store profiling data
interface ProfileData {
  componentId: string;
  renderCount: number;
  lastRenderTime: number;
  averageRenderTime: number;
  maxRenderTime: number;
  totalRenderTime: number;
}

// Track component render performance
const profilingData = new Map<string, ProfileData>();

// Helper for formatting times
const formatTime = (time: number): string => `${time.toFixed(2)}ms`;

// Callback function for React.Profiler
const onRenderCallback = (
  id: string,
  phase: "mount" | "update" | "nested-update",
  actualDuration: number,
  _baseDuration: number,
  _startTime: number,
  _commitTime: number
) => {
  // Skip in production
  if (process.env.NODE_ENV === 'production') return;

  // Get or create profiling data for this component
  let data = profilingData.get(id);
  if (!data) {
    data = {
      componentId: id,
      renderCount: 0,
      lastRenderTime: 0,
      averageRenderTime: 0,
      maxRenderTime: 0,
      totalRenderTime: 0,
    };
    profilingData.set(id, data);
  }

  // Update profiling data
  data.renderCount++;
  data.lastRenderTime = actualDuration;
  data.totalRenderTime += actualDuration;
  data.averageRenderTime = data.totalRenderTime / data.renderCount;
  data.maxRenderTime = Math.max(data.maxRenderTime, actualDuration);

  // Log warnings for slow renders
  if (actualDuration > RENDER_SEVERE_WARNING_THRESHOLD) {
    performanceLogger.warn(
      `[SEVERE] Slow render detected in ${id} (${phase}): ${formatTime(actualDuration)} ` +
      `(Avg: ${formatTime(data.averageRenderTime)}, Max: ${formatTime(data.maxRenderTime)})`
    );
  } else if (actualDuration > RENDER_WARNING_THRESHOLD) {
    performanceLogger.warn(
      `Slow render detected in ${id} (${phase}): ${formatTime(actualDuration)} ` +
      `(Avg: ${formatTime(data.averageRenderTime)})`
    );
  } else {
    performanceLogger.debug(
      `Component ${id} ${phase} rendered in ${formatTime(actualDuration)}`
    );
  }
};

// Get all profiling data
export const getProfilingData = (): ProfileData[] => {
  return Array.from(profilingData.values());
};

// Reset all profiling data
export const resetProfilingData = (): void => {
  profilingData.clear();
};

// Component to visualize profiling data
export const ProfilingDisplay: React.FC<{ show: boolean }> = ({ show }) => {
  const [data, setData] = useState<ProfileData[]>([]);
  
  // Update the display every second
  useEffect(() => {
    if (!show) return;
    
    const interval = setInterval(() => {
      setData(getProfilingData());
    }, 1000);
    
    return () => clearInterval(interval);
  }, [show]);
  
  if (!show) return null;

  return (
    <div 
      style={{
        position: 'fixed',
        bottom: '10px',
        left: '10px',
        zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontFamily: 'monospace',
        fontSize: '12px',
        maxHeight: '30vh',
        maxWidth: '50vw',
        overflow: 'auto',
      }}
    >
      <h4 style={{ margin: '0 0 8px 0' }}>React Profiler Data</h4>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '2px 8px', borderBottom: '1px solid #666' }}>Component</th>
            <th style={{ textAlign: 'right', padding: '2px 8px', borderBottom: '1px solid #666' }}>Renders</th>
            <th style={{ textAlign: 'right', padding: '2px 8px', borderBottom: '1px solid #666' }}>Last</th>
            <th style={{ textAlign: 'right', padding: '2px 8px', borderBottom: '1px solid #666' }}>Avg</th>
            <th style={{ textAlign: 'right', padding: '2px 8px', borderBottom: '1px solid #666' }}>Max</th>
          </tr>
        </thead>
        <tbody>
          {data
            .sort((a, b) => b.lastRenderTime - a.lastRenderTime)
            .map(item => (
              <tr key={item.componentId}>
                <td style={{ padding: '2px 8px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.componentId}
                </td>
                <td style={{ textAlign: 'right', padding: '2px 8px' }}>{item.renderCount}</td>
                <td 
                  style={{ 
                    textAlign: 'right', 
                    padding: '2px 8px',
                    color: item.lastRenderTime > RENDER_SEVERE_WARNING_THRESHOLD
                      ? '#ff6b6b'
                      : item.lastRenderTime > RENDER_WARNING_THRESHOLD
                        ? '#ffd166'
                        : '#a2ff86'
                  }}
                >
                  {formatTime(item.lastRenderTime)}
                </td>
                <td style={{ textAlign: 'right', padding: '2px 8px' }}>{formatTime(item.averageRenderTime)}</td>
                <td style={{ textAlign: 'right', padding: '2px 8px' }}>{formatTime(item.maxRenderTime)}</td>
              </tr>
            ))}
        </tbody>
      </table>
      <div style={{ marginTop: '8px' }}>
        <button 
          onClick={resetProfilingData}
          style={{
            background: '#444',
            border: 'none',
            color: 'white',
            padding: '4px 8px',
            borderRadius: '3px',
            cursor: 'pointer'
          }}
        >
          Reset Data
        </button>
      </div>
    </div>
  );
};

// Component wrapper to profile a component
interface ProfiledComponentProps extends PropsWithChildren {
  id: string;
}

export const ProfiledComponent: React.FC<ProfiledComponentProps> = ({ 
  id, 
  children 
}) => {
  if (process.env.NODE_ENV === 'production') {
    return <>{children}</>;
  }
  
  return (
    <React.Profiler id={id} onRender={onRenderCallback}>
      {children}
    </React.Profiler>
  );
};

// Hook to enable/disable profiling
export const useProfiler = (): { showProfiler: boolean; toggleProfiler: () => void } => {
  const [showProfiler, setShowProfiler] = useState(false);
  
  // Toggle with keyboard shortcut Ctrl+Shift+P
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setShowProfiler(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  return {
    showProfiler,
    toggleProfiler: useCallback(() => setShowProfiler(prev => !prev), []),
  };
}; 
